import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, Eye, EyeOff, Mail, Lock, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Logo URLs - CloudGM Business (Light/Dark variants)
const LOGO_LIGHT = "/logo-light.png"; // Black text for light theme
const LOGO_DARK = "/logo-dark.png";   // White text for dark theme

export default function Login() {
  const { t, language } = useLanguage();
  const { isDarkMode } = useTheme();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    platform: 'cloudgm_business'
  });
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/login`, formData);
      localStorage.setItem('business_token', response.data.access_token);
      localStorage.setItem('business_user', JSON.stringify(response.data.user));
      toast.success(t('loginSuccess'));
      window.dispatchEvent(new Event('userLoggedIn'));
      setTimeout(() => {
        window.location.href = '/dashboard';
      }, 300);
    } catch (error) {
      const detail = error.response?.data?.detail;
      const errorMessage = typeof detail === 'string' 
        ? detail 
        : Array.isArray(detail) 
          ? detail.map(d => d.msg).join(', ')
          : t('invalidCredentials');
      toast.error(errorMessage);
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (tokenResponse) => {
    setGoogleLoading(true);
    try {
      const userInfoResponse = await axios.get('https://www.googleapis.com/oauth2/v3/userinfo', {
        headers: { Authorization: `Bearer ${tokenResponse.access_token}` }
      });

      const response = await axios.post(`${BACKEND_URL}/api/auth/google`, {
        credential: null,
        access_token: tokenResponse.access_token,
        user_info: userInfoResponse.data
      });

      localStorage.setItem('business_token', response.data.access_token);
      localStorage.setItem('business_user', JSON.stringify(response.data.user));
      toast.success(language === 'ar' ? 'تم تسجيل الدخول بنجاح' : 'Login successful');
      window.dispatchEvent(new Event('userLoggedIn'));
      setTimeout(() => {
        window.location.href = '/dashboard';
      }, 300);
    } catch (error) {
      console.error('Google auth error:', error);
      const detail = error?.response?.data?.detail;
      // Structured detail from backend (dict) → use localized message
      if (detail && typeof detail === 'object') {
        const msg = language === 'ar' ? detail.message_ar : detail.message_en;
        if (detail.code === 'GOOGLE_NOT_LINKED') {
          toast.error(msg, {
            duration: 6000,
            action: {
              label: language === 'ar' ? 'إنشاء حساب' : 'Register',
              onClick: () => navigate('/register')
            }
          });
        } else {
          toast.error(msg || (language === 'ar' ? 'فشل تسجيل الدخول بجوجل' : 'Google login failed'));
        }
      } else {
        toast.error(detail || (language === 'ar' ? 'فشل تسجيل الدخول بجوجل' : 'Google login failed'));
      }
      setGoogleLoading(false);
    }
  };

  const googleLogin = useGoogleLogin({
    onSuccess: handleGoogleSuccess,
    onError: () => toast.error(language === 'ar' ? 'فشل تسجيل الدخول بجوجل' : 'Google login failed'),
  });

  // iter-229: Apple login
  const [appleLoading, setAppleLoading] = useState(false);
  useEffect(() => {
    if (document.getElementById('apple-signin-sdk')) return;
    const s = document.createElement('script');
    s.id = 'apple-signin-sdk';
    s.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
    s.async = true;
    document.head.appendChild(s);
  }, []);

  const handleAppleLogin = async () => {
    if (!window.AppleID) {
      toast.error(language === 'ar' ? 'SDK Apple لم يُحمَّل بعد' : 'Apple SDK not ready');
      return;
    }
    setAppleLoading(true);
    try {
      window.AppleID.auth.init({
        clientId: process.env.REACT_APP_APPLE_CLIENT_ID,
        scope: 'name email',
        // Popup mode doesn't need a redirectURI — the id_token is
        // returned directly from the SDK so the login works across
        // every environment (preview, custom domains, production).
        usePopup: true,
      });
      const res = await window.AppleID.auth.signIn();
      const { data } = await axios.post(`${BACKEND_URL}/api/auth/apple`, {
        id_token: res.authorization.id_token,
        user: res.user,
      });
      localStorage.setItem('business_token', data.access_token);
      localStorage.setItem('business_user', JSON.stringify(data.user));
      toast.success(language === 'ar' ? 'تم تسجيل الدخول بنجاح' : 'Signed in');
      window.location.href = '/dashboard';
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (error?.error === 'popup_closed_by_user') {
        toast.error(language === 'ar' ? 'تم إلغاء تسجيل الدخول' : 'Sign-in cancelled');
      } else {
        toast.error(
          (typeof detail === 'string' ? detail : null) ||
          (language === 'ar' ? 'فشل تسجيل الدخول عبر Apple' : 'Apple login failed')
        );
      }
    } finally {
      setAppleLoading(false);
    }
  };

  return (
    <div className={`min-h-screen flex items-center justify-center px-4 py-12 relative overflow-hidden cgm-page`}>
      {/* Background Effects */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 start-0 w-[600px] h-[600px] rounded-full bg-[#38BDF8]/10 blur-[150px]" />
        <div className="absolute bottom-0 end-0 w-[500px] h-[500px] rounded-full bg-[#F97316]/10 blur-[130px]" />
        {/* Grid Pattern */}
        <div 
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `linear-gradient(${isDarkMode ? '#fff' : '#000'} 1px, transparent 1px), linear-gradient(90deg, ${isDarkMode ? '#fff' : '#000'} 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }}
        />
      </div>

      <div className="max-w-md w-full relative z-10">
        {/* Back Button */}
        <Link 
          to="/" 
          className={`inline-flex items-center gap-2 mb-6 text-sm font-medium transition-colors ${
            isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <ArrowLeft className="h-4 w-4 rtl:rotate-180" />
          {language === 'ar' ? 'العودة للرئيسية' : 'Back to Home'}
        </Link>

        {/* Header */}
        <div className="text-center mb-8 cgm-reveal">
          <Link to="/" className="inline-block mb-6">
            <img src={isDarkMode ? LOGO_DARK : LOGO_LIGHT} alt="CloudGM" className="h-12 mx-auto" />
          </Link>
          <h1 className="text-3xl sm:text-4xl font-black mb-2 cgm-text-gradient-animated" style={{ fontFamily: 'Cairo, sans-serif' }} data-testid="login-title">
            {t('loginTitle')}
          </h1>
          <p className={`${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
            {language === 'ar' ? 'تسجيل دخول التجار' : 'Merchant Login'}
          </p>
        </div>

        {/* Login Card */}
        <div className="cgm-ai-card-elevated cgm-reveal" style={{ animationDelay: '100ms' }}>
          {/* Google Login Button */}
          <div className="mb-6">
            <button
              onClick={() => googleLogin()}
              disabled={googleLoading}
              className={`w-full flex items-center justify-center gap-3 px-4 py-3.5 rounded-xl font-medium transition-all duration-300 disabled:opacity-50 ${
                isDarkMode 
                  ? 'bg-white/10 hover:bg-white/20 text-white border border-white/10' 
                  : 'bg-white hover:bg-gray-50 text-gray-700 border-2 border-gray-200 hover:border-gray-300'
              }`}
              data-testid="google-login-btn"
            >
              {googleLoading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <>
                  <svg className="h-5 w-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  <span>
                    {language === 'ar' ? 'تسجيل الدخول بـ Google' : 'Sign in with Google'}
                  </span>
                </>
              )}
            </button>

            {/* iter-229: Apple Login button */}
            <button
              onClick={handleAppleLogin}
              disabled={appleLoading}
              className={`mt-3 w-full flex items-center justify-center gap-3 px-4 py-3.5 rounded-xl font-medium transition-colors duration-300 disabled:opacity-50 ${
                isDarkMode
                  ? 'bg-white/10 hover:bg-white/20 text-white border border-white/10'
                  : 'bg-black hover:bg-gray-900 text-white border-2 border-black'
              }`}
              data-testid="apple-login-btn"
            >
              {appleLoading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <>
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.05 20.28c-.98.95-2.05.86-3.08.43-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.43C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                  </svg>
                  <span>{language === 'ar' ? 'تسجيل الدخول بـ Apple' : 'Sign in with Apple'}</span>
                </>
              )}
            </button>
          </div>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className={`w-full border-t ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`}></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className={`px-4 ${isDarkMode ? 'bg-[#131B2C] text-gray-500' : 'bg-white text-gray-500'}`}>
                {language === 'ar' ? 'أو' : 'OR'}
              </span>
            </div>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {t('email')}
              </Label>
              <div className="relative mt-2">
                <Mail className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                <Input
                  id="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className={`pl-10 py-3 rounded-xl ${
                    isDarkMode 
                      ? 'bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-[#38BDF8]' 
                      : 'bg-white border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-[#38BDF8]'
                  }`}
                  dir="ltr"
                  placeholder={language === 'ar' ? 'أدخل بريدك الإلكتروني' : 'Enter your email'}
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {t('password')}
                </Label>
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-[#38BDF8] hover:text-[#F97316] transition-colors" 
                  data-testid="forgot-password-link"
                >
                  {language === 'ar' ? 'نسيت كلمة المرور؟' : 'Forgot Password?'}
                </Link>
              </div>
              <div className="relative mt-2">
                <Lock className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className={`pl-10 pr-10 py-3 rounded-xl ${
                    isDarkMode 
                      ? 'bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-[#38BDF8]' 
                      : 'bg-white border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-[#38BDF8]'
                  }`}
                  dir="ltr"
                  placeholder={language === 'ar' ? 'أدخل كلمة المرور' : 'Enter your password'}
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={`absolute top-1/2 -translate-y-1/2 right-3 transition-colors ${
                    isDarkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'
                  }`}
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="cgm-cta-primary w-full !py-3.5 !text-base disabled:opacity-50"
              data-testid="login-submit-btn"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : t('login')}
            </button>
          </form>

          {/* Register Link */}
          <div className="mt-6 text-center">
            <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {t('noAccount')}{' '}
              <Link 
                to="/register" 
                className="text-[#38BDF8] hover:text-[#F97316] font-medium transition-colors" 
                data-testid="register-link"
              >
                {t('createNewAccount')}
              </Link>
            </p>
          </div>

          {/* Customer Portal Link */}
          <div className={`mt-6 pt-6 border-t text-center ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`}>
            <p className={`text-sm mb-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              {t('areYouCustomer')}
            </p>
            <a 
              href={process.env.REACT_APP_CUSTOMER_PORTAL_URL || "/"} 
              className="text-sm text-[#38BDF8] hover:text-[#F97316] transition-colors"
            >
              {t('loginToCustomerPlatform')}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
